// Reader side of the ski v3 stored carriers (consumer inventory Sections 3 and 6, contract
// Section 17). Pure parsing: no I/O, no clock. Every reader that shows v3 detail reads it
// through these helpers from data it already selects, so no new database reads are needed.
//
// Carriers read here (all optional; a revision 2 row simply has none of them):
// - condition_scores score provenance, depth block, sports[sport]: `cls`, `p10`/`p90` (mirrored in
//   `lo`/`hi` with rangeKind "p10-p90"), `status`, `statusReason`, `flags`, and for piste `q`
//   (quality before the extent factor), `x` and `ext {km, pct, src}` (SkiV3SportProvenance in
//   ski/legacy-adapter.ts).
//   `score_provenance.recalculatedSports[sport].snow` replaces it after a saved-input rescore.
// - condition_scores.score_provenance.ski: the compact resort block {v:3, x, e, km, st, cc, cr, ir}.
// - forecast_data.forecast[i]: `skiBands` ({piste, "off-piste", touring} of StoredSkiBand[]),
//   `skiTimeBlocks` ({sport: StoredTimeBlock[]}), `touringPlan`, `offPisteRose`, and the day's
//   own `score_provenance` (same shape as the row's).
// - daily_observations.sport_hourly_scores.sportDailyEvidence[sport]: `bands`, `cls`, `range`
//   ([lo, hi] 0 to 100), `status`, `surface` (StoredTimeBlock[]), `plan` (touring), `rose`
//   (off-piste), and `flags` and `q` (piste) when a writer stores them.
//
// A withheld sport (Closed, out of season, or no lift access for piste and off-piste) is written
// with null `p10`/`p90` and `lo`/`hi`, no piste `q` and no daily evidence `range`. Rows written
// before that still carry the "if open" values, so these readers drop `lo`, `hi` and `quality`
// whenever the parsed status withholds the score (the owner decision: Closed shows no number).
// Every consumer of these helpers (detail, boards, comparison, UI) therefore gets no range for
// a withheld sport.
//
// Scales: band scores, lo, hi and quality are 0 to 100 integers; the extent factor is 0.6 to 1.
import type { StoredSkiBand, StoredSkiFlag, StoredTimeBlock } from "./ski/legacy-adapter";
import type { AspectSector, ConfidenceClass, SkiSport } from "./ski/types";
import {
  SKI_SPORT_KEYS,
  type SkiPublishedStatus,
  type SkiPublishedStatusReason,
} from "./ski-labels-v3";

export type { StoredSkiBand, StoredSkiFlag, StoredTimeBlock };

export type SkiExtentBasis = "evidence" | "prior" | "mixed";

/** Piste extent: factor X, share of terrain open (0 to 100), open km and where it came from. */
export type SkiV3Extent = Readonly<{
  factor: number | null;
  openPct: number | null;
  openKm: number | null;
  basis: SkiExtentBasis | null;
}>;

/** Touring plan (consumer inventory 6.2): local decimal hours, metres. */
export type SkiTouringPlan = Readonly<{
  start: number;
  descend: number;
  aspects: readonly AspectSector[];
  snowlineM: number | null;
  carryM: number | null;
  /** Wet-snow ceiling (0 to 1 wet index, or metres when the writer stores a height); null when absent. */
  ceiling: number | null;
}>;

/** Off-piste aspect rose per band key: 0 to 100 per aspect sector. */
export type SkiOffPisteRose = Readonly<Record<string, Readonly<Partial<Record<AspectSector, number>>>>>;

/** v3 fields for one ski sport, read from stored carriers. All optional. */
export type SkiV3SportFields = {
  status?: SkiPublishedStatus;
  statusReason?: SkiPublishedStatusReason;
  cls?: ConfidenceClass;
  /** P10, 0 to 100 integer. */
  lo?: number;
  /** P90, 0 to 100 integer. */
  hi?: number;
  /** Stored flags in a fixed order (cover-risk, ice-risk, cover-assumed, limited-terrain); absent when none. */
  flags?: readonly StoredSkiFlag[];
  /** Piste only: quality on open terrain before the extent factor (`q`), 0 to 100 integer. */
  quality?: number;
  /** Piste only. */
  extent?: SkiV3Extent;
  bands?: readonly StoredSkiBand[];
  timeBlocks?: readonly StoredTimeBlock[];
  /** Touring only. */
  touringPlan?: SkiTouringPlan;
  /** Off-piste only. */
  offPisteRose?: SkiOffPisteRose;
};

export type SkiV3BySport = Partial<Record<SkiSport, SkiV3SportFields>>;

type Json = Record<string, unknown>;

function rec(value: unknown): Json | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Json : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const STATUSES = new Set<SkiPublishedStatus>(["open", "closed", "unknown", "out-of-season"]);
const REASONS = new Set<SkiPublishedStatusReason>(["resort-closed", "lifts-closed", "run-reports", "season", "no-lift-access"]);
const EXTENT_SOURCES = new Set<SkiExtentBasis>(["evidence", "prior", "mixed"]);
const ASPECTS = new Set<AspectSector>(["N", "E", "S", "W"]);
const COVER_WORDS = new Set(["full", "thin", "marginal", "patchy", "bare", "ribbon", "closed"]);
const SURFACE_CLASSES = new Set(["powder", "groomed", "hard", "corn", "slush", "chop"]);
const WITHHELD = new Set(["closed", "no_lift_access"]);
/** Fixed flag order, so readers render chips in the same order whatever the writer stored. */
const FLAG_ORDER: readonly StoredSkiFlag[] = ["cover-risk", "ice-risk", "cover-assumed", "limited-terrain"];

/** Stored status, accepting the engine's no_lift_access as closed with reason no-lift-access. */
function parseStatus(value: unknown): { status?: SkiPublishedStatus; reason?: SkiPublishedStatusReason } {
  if (value === "no_lift_access") return { status: "closed", reason: "no-lift-access" };
  return typeof value === "string" && STATUSES.has(value as SkiPublishedStatus)
    ? { status: value as SkiPublishedStatus }
    : {};
}

function parseReason(value: unknown): SkiPublishedStatusReason | undefined {
  return typeof value === "string" && REASONS.has(value as SkiPublishedStatusReason)
    ? value as SkiPublishedStatusReason
    : undefined;
}

function parseClass(value: unknown): ConfidenceClass | undefined {
  return value === "A" || value === "B" || value === "C" ? value : undefined;
}

function int100(value: unknown): number | null {
  const n = num(value);
  return n === null ? null : Math.max(0, Math.min(100, Math.round(n)));
}

/** Known flags in FLAG_ORDER; null when none are stored. */
function parseFlags(value: unknown): StoredSkiFlag[] | null {
  if (!Array.isArray(value)) return null;
  const stored = new Set(value as unknown[]);
  const flags = FLAG_ORDER.filter((flag) => stored.has(flag));
  return flags.length ? flags : null;
}

/** True when the parsed status withholds the sport's score (the rule of skiV3IsClosed). */
function withholds(sport: SkiSport, status: SkiPublishedStatus | undefined, reason: SkiPublishedStatusReason | undefined): boolean {
  if (status === "closed" && reason === "no-lift-access") return sport !== "touring";
  return status === "closed" || status === "out-of-season";
}

/** The v3 fields every carrier shares: class, status and reason, the P10 to P90 range, flags and the piste quality. */
function commonFields(
  sport: SkiSport,
  cls: ConfidenceClass,
  block: Json,
  lo: number | null,
  hi: number | null,
): SkiV3SportFields {
  const { status, reason: statusFromEngine } = parseStatus(block.status);
  const reason = parseReason(block.statusReason) ?? statusFromEngine;
  // A withheld sport exposes no range and no quality: older rows still store the "if open"
  // values, and Closed never shows a number (owner decision).
  const withheld = withholds(sport, status, reason);
  const range = !withheld && lo !== null && hi !== null ? { lo: Math.min(lo, hi), hi: Math.max(lo, hi) } : null;
  const flags = parseFlags(block.flags);
  const quality = sport === "piste" && !withheld ? int100(block.q) : null;
  return {
    cls,
    ...(status ? { status } : {}),
    ...(reason ? { statusReason: reason } : {}),
    ...(range ?? {}),
    ...(flags ? { flags } : {}),
    ...(quality !== null ? { quality } : {}),
  };
}

function parseBlock(value: unknown): StoredTimeBlock | null {
  if (!Array.isArray(value) || value.length !== 3) return null;
  const [from, to, cls] = value as unknown[];
  const f = num(from);
  const t = num(to);
  if (f === null || t === null || !(t > f) || typeof cls !== "string" || !SURFACE_CLASSES.has(cls)) return null;
  return [f, t, cls as StoredTimeBlock[2]];
}

/** Stored time blocks [fromHour, toHour, surfaceClass]; invalid entries are dropped. */
export function parseStoredTimeBlocks(value: unknown): StoredTimeBlock[] | null {
  if (!Array.isArray(value)) return null;
  const out = value.map(parseBlock).filter((b): b is StoredTimeBlock => b !== null);
  return out.length ? out : null;
}

/** One stored band; null when the core fields are missing or out of range. */
export function parseStoredSkiBand(value: unknown): StoredSkiBand | null {
  const b = rec(value);
  if (!b || typeof b.b !== "string" || b.b.length === 0 || b.b.length > 8) return null;
  const z = num(b.z);
  const lo = int100(b.lo);
  const hi = int100(b.hi);
  const s = b.s === null ? null : int100(b.s);
  if (z === null || lo === null || hi === null || (b.s !== null && s === null)) return null;
  if (typeof b.cov !== "string" || !COVER_WORDS.has(b.cov)) return null;
  const srf = Array.isArray(b.srf)
    ? (b.srf as unknown[]).flatMap((entry): [StoredTimeBlock[2], number][] => {
        if (!Array.isArray(entry) || entry.length !== 2) return [];
        const [cls, pct] = entry as unknown[];
        const p = int100(pct);
        return typeof cls === "string" && SURFACE_CLASSES.has(cls) && p !== null ? [[cls as StoredTimeBlock[2], p]] : [];
      })
    : [];
  const blk = parseStoredTimeBlocks(b.blk);
  const st = typeof b.st === "string" && WITHHELD.has(b.st) ? b.st as StoredSkiBand["st"] : undefined;
  const fl = num(b.fl);
  const ext = int100(b.ext);
  return {
    b: b.b as StoredSkiBand["b"],
    z,
    // A withheld band never shows a number, whatever `s` says.
    s: st ? null : s,
    lo: Math.min(lo, hi),
    hi: Math.max(lo, hi),
    cov: b.cov as StoredSkiBand["cov"],
    ...(srf.length ? { srf } : {}),
    ...(blk && blk.length ? { blk } : {}),
    ...(st ? { st } : {}),
    ...(fl !== null ? { fl } : {}),
    ...(ext !== null ? { ext } : {}),
  };
}

/** Stored bands of one sport, lowest first; null when none parse. */
export function parseStoredSkiBands(value: unknown): StoredSkiBand[] | null {
  if (!Array.isArray(value)) return null;
  const out = value.map(parseStoredSkiBand).filter((b): b is StoredSkiBand => b !== null);
  return out.length ? out : null;
}

/** `forecast[i].skiBands`: `{ piste, "off-piste", touring }`, each StoredSkiBand[]. */
export function parseStoredSkiBandsBySport(value: unknown): Partial<Record<SkiSport, StoredSkiBand[]>> | null {
  const r = rec(value);
  if (!r) return null;
  const out: Partial<Record<SkiSport, StoredSkiBand[]>> = {};
  for (const sport of SKI_SPORT_KEYS) {
    const bands = parseStoredSkiBands(r[sport]);
    if (bands) out[sport] = bands;
  }
  return Object.keys(out).length ? out : null;
}

function parseAspects(value: unknown): AspectSector[] {
  return Array.isArray(value)
    ? (value as unknown[]).filter((a): a is AspectSector => typeof a === "string" && ASPECTS.has(a as AspectSector))
    : [];
}

/**
 * Touring plan: the compact stored shape {start, descend, aspects, snowlineM, carryM, ceiling}
 * or the engine's TourPlanSummary {startBy, descendBy, aspectOrder, snowlineM, carryM,
 * wetSnowIndex}. Null when start or descend is missing.
 */
export function parseStoredTouringPlan(value: unknown): SkiTouringPlan | null {
  const p = rec(value);
  if (!p) return null;
  const start = num(p.start ?? p.startBy);
  const descend = num(p.descend ?? p.descendBy);
  if (start === null || descend === null || start < 0 || start > 24 || descend < 0 || descend > 24) return null;
  return {
    start,
    descend,
    aspects: parseAspects(p.aspects ?? p.aspectOrder),
    snowlineM: num(p.snowlineM),
    carryM: num(p.carryM),
    ceiling: num(p.ceiling ?? p.wetSnowIndex),
  };
}

/** Off-piste rose: {bandKey: {N, E, S, W}} with 0 to 100 values; null when empty. */
export function parseStoredOffPisteRose(value: unknown): SkiOffPisteRose | null {
  const r = rec(value);
  if (!r) return null;
  const out: Record<string, Partial<Record<AspectSector, number>>> = {};
  for (const [band, sectors] of Object.entries(r)) {
    const s = rec(sectors);
    if (!s || band.length > 8) continue;
    const entry: Partial<Record<AspectSector, number>> = {};
    for (const aspect of ASPECTS) {
      const v = int100(s[aspect]);
      if (v !== null) entry[aspect] = v;
    }
    if (Object.keys(entry).length) out[band] = entry;
  }
  return Object.keys(out).length ? out : null;
}

function sportProvenance(provenance: unknown, sport: SkiSport): Json | null {
  const p = rec(provenance);
  if (!p) return null;
  const recalculated = rec(rec(rec(p.recalculatedSports)?.[sport])?.snow);
  return recalculated ?? rec(rec(rec(p.depth)?.sports)?.[sport]);
}

/**
 * v3 fields in a per-sport provenance block: class, status, the P10 to P90 range, flags, and
 * for piste the quality before extent (`q`) and the extent. Returns an empty object for
 * revision 2 provenance (no `cls`), so legacy ranges are never presented as v3 P10 and P90.
 */
export function skiV3FromProvenance(provenance: unknown, sport: SkiSport): SkiV3SportFields {
  const sp = sportProvenance(provenance, sport);
  const cls = parseClass(sp?.cls);
  if (!sp || !cls) return {};
  const out: SkiV3SportFields = commonFields(sport, cls, sp, int100(sp.p10 ?? sp.lo), int100(sp.p90 ?? sp.hi));
  if (sport === "piste") {
    const ext = rec(sp.ext);
    const resort = rec(rec(provenance)?.ski);
    const factor = num(sp.x) ?? num(resort?.x);
    const openKm = num(ext?.km) ?? num(resort?.km);
    const openPct = num(ext?.pct) ?? (num(resort?.e) !== null ? Math.round((num(resort?.e) as number) * 100) : null);
    const src = typeof ext?.src === "string" && EXTENT_SOURCES.has(ext.src as SkiExtentBasis) ? ext.src as SkiExtentBasis : null;
    if (factor !== null || openKm !== null || openPct !== null) {
      out.extent = { factor, openPct, openKm, basis: src };
    }
  }
  return out;
}

/** A forecast day as the readers see it (SkiScoredDay plus the v3 carriers). */
export type SkiForecastDayCarriers = Readonly<{
  skiBands?: unknown;
  skiTimeBlocks?: unknown;
  touringPlan?: unknown;
  offPisteRose?: unknown;
  score_provenance?: unknown;
}>;

/**
 * v3 fields for every ski sport on one forecast day. `rowProvenance` (the condition row's
 * score_provenance) is used when the day carries none of its own (day 0 on older writers).
 */
export function skiV3ForForecastDay(day: SkiForecastDayCarriers | null | undefined, rowProvenance?: unknown): SkiV3BySport {
  if (!day) return {};
  const bands = parseStoredSkiBandsBySport(day.skiBands);
  const blocksBySport = rec(day.skiTimeBlocks);
  const plan = parseStoredTouringPlan(day.touringPlan);
  const rose = parseStoredOffPisteRose(day.offPisteRose);
  const provenance = day.score_provenance ?? rowProvenance;
  const out: SkiV3BySport = {};
  for (const sport of SKI_SPORT_KEYS) {
    const fields: SkiV3SportFields = { ...skiV3FromProvenance(provenance, sport) };
    const sportBands = bands?.[sport];
    if (sportBands) fields.bands = sportBands;
    const blocks = parseStoredTimeBlocks(blocksBySport?.[sport]);
    if (blocks) fields.timeBlocks = blocks;
    if (sport === "touring" && plan) fields.touringPlan = plan;
    if (sport === "off-piste" && rose) fields.offPisteRose = rose;
    if (Object.keys(fields).length) out[sport] = fields;
  }
  return out;
}

/**
 * v3 fields for one sport from `sportDailyEvidence[sport]` (history rows), including `flags`
 * and the piste `q` when the writer stored them. Returns an empty object when the evidence is
 * revision 2 (no `cls`).
 */
export function skiV3FromDailyEvidence(evidence: unknown, sport: SkiSport): SkiV3SportFields {
  const e = rec(evidence);
  const cls = parseClass(e?.cls);
  if (!e || !cls) return {};
  const range = Array.isArray(e.range) && e.range.length === 2 ? e.range as unknown[] : null;
  const lo = range ? int100(range[0]) : null;
  const hi = range ? int100(range[1]) : null;
  const bands = parseStoredSkiBands(e.bands);
  const blocks = parseStoredTimeBlocks(e.surface);
  const plan = sport === "touring" ? parseStoredTouringPlan(e.plan) : null;
  const rose = sport === "off-piste" ? parseStoredOffPisteRose(e.rose) : null;
  return {
    ...commonFields(sport, cls, e, lo, hi),
    ...(bands ? { bands } : {}),
    ...(blocks ? { timeBlocks: blocks } : {}),
    ...(plan ? { touringPlan: plan } : {}),
    ...(rose ? { offPisteRose: rose } : {}),
  };
}

/** True when the fields say the sport is Closed (or out of season, or without lift access). */
export function skiV3IsClosed(fields: SkiV3SportFields | null | undefined, sport: SkiSport): boolean {
  if (!fields?.status) return false;
  return withholds(sport, fields.status, fields.statusReason);
}
